import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
    Store, Plus, UserPlus, KeyRound, Power, ChevronRight, Copy, Check, Users, Building2, CircleCheck, CircleSlash, Mail,
    Upload, Truck, Pencil, Inbox, AlertTriangle, MessageSquare, BookUser, FileSpreadsheet, Download,
} from "lucide-react";
import api from "../../utils/api";
import { fmtDateTime, apiError, statusOf, PRIORITIES, ticketNumber, DONE_STATUSES } from "./ticketConfig";
import {
    PageHeader, Button, KpiTile, ObjectStatus, Avatar, SearchField, Field, TableCard,
    EmptyState, MessageStrip, Dialog, surface, inputCls, thCls, tdCls, relTime,
} from "./fiori";

// Random 14-character password for a new customer login (shown once, Hasbro shares it)
const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    const buf = new Uint32Array(14);
    window.crypto.getRandomValues(buf);
    return Array.from(buf, n => chars[n % chars.length]).join("");
};

const loginUrl = () => `${window.location.origin}/login?brand=hasbro`;

const FILTERS = [
    { id: "all", label: "All" },
    { id: "portal", label: "Portal access" },
    { id: "noportal", label: "No portal access" },
    { id: "noemail", label: "No email on file" },
];

// Hasbro's customers in one place: the contact directory (the ship-to email list Hasbro
// uploads) and the customer portal accounts + logins Hasbro creates. Look a customer up,
// see their contact and tickets, and give them portal access (login + password).
export default function HasbroCustomers() {
    const [customers, setCustomers] = useState([]);
    const [directory, setDirectory] = useState({ contacts: [], carriers: [], lastImport: null });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [selectedKey, setSelectedKey] = useState(null);
    const [pendingLogin, setPendingLogin] = useState(null);   // entry key that should open "Add login" once loaded
    const [dialog, setDialog] = useState(null);               // "new" | "import" | "carriers"

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, d] = await Promise.all([api.get("/api/hasbro/customers"), api.get("/api/hasbro/contacts")]);
            setCustomers(c.data.customers || []);
            setDirectory(d.data);
            setError("");
        } catch (err) {
            setError(apiError(err, "Could not load customers."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // One entry per directory account (with its portal account, if any), plus portal
    // accounts that aren't in the directory
    const entries = useMemo(() => {
        const byId = new Map(customers.map(c => [c.id, c]));
        const linked = new Set();
        const list = directory.contacts.map(ct => {
            const customer = ct.customerId ? byId.get(ct.customerId) || null : null;
            if (customer) linked.add(customer.id);
            return { key: `c:${ct.id}`, name: ct.accountName, email: ct.email, contact: ct, customer };
        });
        customers.filter(c => !linked.has(c.id)).forEach(c => list.push({ key: `a:${c.id}`, name: c.name, email: c.contactEmail || null, contact: null, customer: c }));
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [customers, directory.contacts]);

    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return entries.filter(e =>
            (filter === "all" || (filter === "portal" && e.customer) || (filter === "noportal" && !e.customer) || (filter === "noemail" && !e.email))
            && (!s || [e.name, e.email, ...(e.contact?.aliases || [])].some(v => (v || "").toLowerCase().includes(s))));
    }, [entries, q, filter]);
    // A portal-only account that was just added to the contact list keeps its selection
    const selected = entries.find(e => e.key === selectedKey)
        || (selectedKey?.startsWith("a:") && entries.find(e => e.customer?.id === selectedKey.slice(2))) || null;
    const withPortal = entries.filter(e => e.customer).length;
    const logins = customers.reduce((n, c) => n + (c.userCount || 0), 0);
    const noEmail = entries.filter(e => !e.email).length;
    const last = directory.lastImport;

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={[{ label: "Hasbro" }, { label: "Customer care" }]}
                icon={Store}
                title="Customer Accounts"
                subtitle="Look up a customer, see their contact and tickets, and give them a login to raise and follow tickets."
                actions={<>
                    <Button icon={BookUser} onClick={() => setDialog("contacts")}>Contact list{directory.contacts.length ? ` (${directory.contacts.length})` : ""}</Button>
                    <Button icon={Upload} onClick={() => setDialog("import")}>Import contacts</Button>
                    <Button icon={Truck} onClick={() => setDialog("carriers")}>Carrier contacts</Button>
                    <Button variant="emphasized" icon={Plus} onClick={() => setDialog("new")}>New customer</Button>
                </>}
            >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <KpiTile label="Customers" value={entries.length} icon={Building2} tone="information" hint={last ? `${directory.contacts.length} in the contact list` : undefined} />
                    <KpiTile label="With portal access" value={withPortal} total={entries.length} icon={CircleCheck} tone="positive" active={filter === "portal"} onClick={() => setFilter(f => (f === "portal" ? "all" : "portal"))} />
                    <KpiTile label="Customer logins" value={logins} icon={Users} tone="indication" />
                    <KpiTile label="No email on file" value={noEmail} total={entries.length} icon={AlertTriangle} tone={noEmail ? "critical" : "neutral"} active={filter === "noemail"} onClick={() => setFilter(f => (f === "noemail" ? "all" : "noemail"))} />
                </div>
            </PageHeader>

            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {!loading && !last && directory.contacts.length === 0 && (
                <MessageStrip>No contact list yet. <button className="font-semibold underline" onClick={() => setDialog("import")}>Import the ship-to email list</button> (CSV or Excel with Account and Email columns) so customers can be looked up and contacted from the Control Tower.</MessageStrip>
            )}

            {/* Look-up bar */}
            <section className={`${surface} p-4 space-y-3`}>
                <SearchField value={q} onChange={setQ} placeholder="Look up a customer — account name, email, or a name from the reports" className="w-full" />
                <div className="flex flex-wrap items-center gap-2">
                    {FILTERS.map(f => (
                        <button key={f.id} onClick={() => setFilter(f.id)}
                            className={`px-3 h-7 rounded-full text-xs font-semibold border transition-colors ${filter === f.id
                                ? "bg-[#0087C9] border-[#0087C9] text-white" : "border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-[#0087C9]"}`}>
                            {f.label}
                        </button>
                    ))}
                    <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                        {q || filter !== "all" ? `${shown.length} of ${entries.length}` : `${entries.length} customers`}
                        {last && <> · contact list {last.fileName ? `“${last.fileName}” ` : ""}imported {relTime(last.at)}{last.by?.name ? ` by ${last.by.name}` : ""}</>}
                    </span>
                </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_1fr] gap-5 items-start">
                {/* Master list */}
                <section className={`${surface} overflow-hidden ${selected ? "hidden lg:block" : ""}`}>
                    {!loading && entries.length === 0 ? (
                        <EmptyState icon={Store} title="No customers yet" text="Import your contact list, or add a customer, e.g. a retailer you ship to."
                            action={<Button variant="emphasized" icon={Upload} onClick={() => setDialog("import")}>Import contacts</Button>} />
                    ) : (
                        <ul className="max-h-[64vh] overflow-auto divide-y divide-slate-100 dark:divide-white/[0.05]">
                            {loading && entries.length === 0 && [0, 1, 2].map(i => <li key={i} className="p-4"><div className="h-9 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" /></li>)}
                            {shown.map(e => (
                                <li key={e.key}>
                                    <button onClick={() => setSelectedKey(e.key)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors relative ${selected?.key === e.key
                                            ? "bg-[#0087C9]/[0.08] dark:bg-[#0087C9]/15" : "hover:bg-slate-50 dark:hover:bg-white/[0.03]"}`}>
                                        {selected?.key === e.key && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#0087C9]" />}
                                        <Avatar name={e.name} size={36} square />
                                        <span className="flex-1 min-w-0">
                                            <span className="block font-semibold text-sm text-[#1D2D3E] dark:text-white truncate">{e.name}</span>
                                            <span className={`block text-xs truncate ${e.email ? "text-slate-500 dark:text-slate-400" : "text-amber-600 dark:text-amber-300"}`}>{e.email || "No email on file"}</span>
                                        </span>
                                        <PortalStatus customer={e.customer} />
                                        <ChevronRight size={15} className="text-slate-300 shrink-0" />
                                    </button>
                                </li>
                            ))}
                            {!loading && entries.length > 0 && shown.length === 0 && <li className="px-4 py-8 text-center text-sm text-slate-400">No customers match{q ? ` “${q}”` : " this filter"}.</li>}
                        </ul>
                    )}
                </section>

                {/* Detail */}
                {selected ? (
                    <EntryDetail key={selected.key} entry={selected} onChanged={load} onBack={() => setSelectedKey(null)}
                        openAddLogin={pendingLogin === selected.key && !!selected.customer} onAddLoginOpened={() => setPendingLogin(null)}
                        onGiveAccess={() => setPendingLogin(selected.key)} />
                ) : (
                    <section className={`${surface} hidden lg:block`}>
                        <EmptyState icon={Users} title="Select a customer" text="Search or pick a customer on the left to see their contact, tickets and portal logins." />
                    </section>
                )}
            </div>

            {dialog === "new" && <NewCustomerDialog onClose={() => setDialog(null)} onCreated={c => { setDialog(null); load().then(() => setSelectedKey(`a:${c.id}`)); }} />}
            {dialog === "contacts" && (
                <ContactListDialog contacts={directory.contacts} customers={customers} lastImport={last}
                    onClose={() => setDialog(null)} onImport={() => setDialog("import")}
                    onOpen={key => { setDialog(null); setQ(""); setFilter("all"); setSelectedKey(key); }} />
            )}
            {dialog === "import" && <ImportDialog current={directory.contacts.length} onClose={() => setDialog(null)} onImported={load} />}
            {dialog === "carriers" && <CarriersDialog carriers={directory.carriers} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
        </div>
    );
}

function PortalStatus({ customer }) {
    if (!customer) return <ObjectStatus tone="neutral">No access</ObjectStatus>;
    if (customer.status !== "active") return <ObjectStatus tone="neutral" icon={CircleSlash}>Disabled</ObjectStatus>;
    return <ObjectStatus tone="positive">{customer.userCount || 0} login{customer.userCount === 1 ? "" : "s"}</ObjectStatus>;
}

// ── Detail ────────────────────────────────────────────────────────────────────

function EntryDetail({ entry, onChanged, onBack, openAddLogin, onAddLoginOpened, onGiveAccess }) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Directory account without a portal account: create one, then open "Add login"
    const giveAccess = async () => {
        setBusy(true);
        setError("");
        try {
            await api.post("/api/hasbro/customers", { name: entry.name, contactEmail: entry.email || "", contactId: entry.contact.id });
            onGiveAccess();
            await onChanged();
        } catch (err) {
            setError(apiError(err, "Could not give portal access."));
        } finally {
            setBusy(false);
        }
    };

    if (entry.customer) {
        return <CustomerDetail customer={entry.customer} entry={entry} onChanged={onChanged} onBack={onBack} openAddLogin={openAddLogin} onAddLoginOpened={onAddLoginOpened} />;
    }
    return (
        <div className="space-y-5 min-w-0">
            <section className={`${surface} p-5`}>
                <button onClick={onBack} className="lg:hidden text-xs text-[#0065A0] dark:text-[#6FD0F5] mb-3">‹ All customers</button>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <Avatar name={entry.name} size={56} square />
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-bold text-[#1D2D3E] dark:text-white truncate">{entry.name}</h2>
                            <ObjectStatus tone="neutral">No portal access</ObjectStatus>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">In the contact list{entry.contact.sourceRow ? ` · row ${entry.contact.sourceRow}` : ""}</p>
                    </div>
                    <Button variant="emphasized" icon={KeyRound} onClick={giveAccess} disabled={busy}>{busy ? "Setting up…" : "Give portal access"}</Button>
                </div>
            </section>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            <ContactSection entry={entry} onChanged={onChanged} />
            <section className={surface}>
                <EmptyState icon={KeyRound} title="No portal access yet"
                    text={`Give ${entry.name} a login (email + password) so they can raise tickets about ASNs, deliveries and PODs, and follow them up.`}
                    action={<Button variant="emphasized" icon={KeyRound} onClick={giveAccess} disabled={busy}>Give portal access</Button>} />
            </section>
        </div>
    );
}

function ContactSection({ entry, onChanged }) {
    const c = entry.contact;
    const [editing, setEditing] = useState(false);
    const [email, setEmail] = useState(entry.email || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            if (c) await api.patch(`/api/hasbro/contacts/${c.id}`, { email });
            else await api.post("/api/hasbro/contacts", { accountName: entry.name, email, customerId: entry.customer?.id });
            setEditing(false);
            await onChanged();
        } catch (err) {
            setError(apiError(err, "Could not save the contact."));
        } finally {
            setSaving(false);
        }
    };
    const copy = () => navigator.clipboard?.writeText(entry.email).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
    const dataNotes = (c?.notes || []).filter(n => n !== "missing email");

    return (
        <TableCard title="Contact" actions={!editing && <Button variant="transparent" icon={Pencil} onClick={() => { setEmail(entry.email || ""); setEditing(true); }}>{entry.email ? "Edit" : "Add email"}</Button>}>
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div className="sm:col-span-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Email</p>
                    {editing ? (
                        <div className="flex flex-wrap gap-2">
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} maxLength={254} className={`${inputCls} flex-1 min-w-[220px]`} placeholder="name@customer.com" autoFocus />
                            <Button variant="emphasized" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                            <Button variant="transparent" onClick={() => setEditing(false)}>Cancel</Button>
                        </div>
                    ) : entry.email ? (
                        <span className="flex flex-wrap items-center gap-2">
                            <a href={`mailto:${entry.email}`} className="font-semibold text-[#0065A0] dark:text-[#6FD0F5] hover:underline break-all"><Mail size={13} className="inline mr-1.5 -mt-0.5" />{entry.email}</a>
                            <button onClick={copy} className="text-slate-400 hover:text-[#0087C9]" title="Copy email">{copied ? <Check size={14} /> : <Copy size={14} />}</button>
                        </span>
                    ) : (
                        <span className="text-amber-600 dark:text-amber-300">No email on file</span>
                    )}
                    {error && <p className="text-xs text-red-600 dark:text-red-300 mt-1">{error}</p>}
                </div>
                {c?.sharedWith?.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Shared inbox</p>
                        <p className="text-slate-600 dark:text-slate-300 flex items-start gap-1.5"><Inbox size={14} className="mt-0.5 shrink-0" /> Also used by {c.sharedWith.join(", ")}</p>
                    </div>
                )}
                {c?.aliases?.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Also appears in the reports as</p>
                        <p className="text-slate-600 dark:text-slate-300">{c.aliases.join(" · ")}</p>
                    </div>
                )}
                {dataNotes.length > 0 && (
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Data notes</p>
                        <p className="text-slate-600 dark:text-slate-300">{dataNotes.join(" · ")}</p>
                    </div>
                )}
                {!c && <p className="sm:col-span-2 text-xs text-slate-500 dark:text-slate-400">Not in the uploaded contact list{entry.email ? " — the email above is the one saved on the portal account" : ""}. Saving an email adds this customer to the list so the Control Tower can find it.</p>}
            </div>
        </TableCard>
    );
}

function CustomerDetail({ customer, entry, onChanged, onBack, openAddLogin, onAddLoginOpened }) {
    const [users, setUsers] = useState(null);
    const [error, setError] = useState("");
    const [adding, setAdding] = useState(false);
    const [created, setCreated] = useState(null);     // { email, password } shown once
    const [busy, setBusy] = useState(false);
    const isActive = customer.status === "active";

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/hasbro/customers/${customer.id}/users`);
            setUsers(data.users || []);
        } catch (err) {
            setError(apiError(err, "Could not load logins."));
        }
    }, [customer.id]);

    useEffect(() => { load(); }, [load]);
    // Just given portal access → go straight to creating the first login
    useEffect(() => {
        if (openAddLogin) { setAdding(true); onAddLoginOpened(); }
    }, [openAddLogin, onAddLoginOpened]);

    const run = async (fn, fallback) => {
        setBusy(true);
        setError("");
        try { await fn(); }
        catch (err) { setError(apiError(err, fallback)); }
        finally { setBusy(false); }
    };
    const toggleCustomer = () => {
        if (isActive && !window.confirm(`Disable ${customer.name}? Their logins stop working until you enable it again.`)) return;
        run(async () => { await api.patch(`/api/hasbro/customers/${customer.id}`, { status: isActive ? "disabled" : "active" }); onChanged(); }, "Could not update the customer.");
    };
    const resetPassword = u => {
        if (!window.confirm(`Set a new password for ${u.email}? Their current password stops working and they're signed out.`)) return;
        const password = generatePassword();
        run(async () => { await api.patch(`/api/hasbro/customer-users/${u.uid}`, { password }); setCreated({ email: u.email, password }); }, "Could not reset the password.");
    };
    const toggleUser = u => run(async () => {
        await api.patch(`/api/hasbro/customer-users/${u.uid}`, { status: u.status === "disabled" ? "active" : "disabled" });
        await load();
    }, "Could not update the login.");

    return (
        <div className="space-y-5 min-w-0">
            {/* Object header */}
            <section className={`${surface} p-5`}>
                <button onClick={onBack} className="lg:hidden text-xs text-[#0065A0] dark:text-[#6FD0F5] mb-3">‹ All customers</button>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <Avatar name={customer.name} size={56} square />
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-xl font-bold text-[#1D2D3E] dark:text-white truncate">{customer.name}</h2>
                            <ObjectStatus tone={isActive ? "positive" : "neutral"} icon={isActive ? CircleCheck : CircleSlash}>{isActive ? "Portal active" : "Portal disabled"}</ObjectStatus>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-1 text-xs text-slate-500 dark:text-slate-400">
                            <span>Portal account added {relTime(customer.createdAt)}{customer.createdBy?.name && ` by ${customer.createdBy.name}`}</span>
                            {entry.contact && <span>In the contact list</span>}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button variant={isActive ? "negative" : "default"} icon={Power} onClick={toggleCustomer} disabled={busy}>{isActive ? "Disable" : "Enable"}</Button>
                        {isActive && <Button variant="emphasized" icon={UserPlus} onClick={() => { setCreated(null); setAdding(true); }}>Add login</Button>}
                    </div>
                </div>
            </section>

            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {!isActive && <MessageStrip>This customer is disabled — its logins can't sign in until you enable it.</MessageStrip>}
            {created && <CredentialsStrip created={created} onClose={() => setCreated(null)} />}

            <ContactSection entry={entry} onChanged={onChanged} />

            <TableCard title="Logins" count={users?.length}>
                {users === null ? (
                    <div className="p-5 space-y-3">{[0, 1].map(i => <div key={i} className="h-8 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" />)}</div>
                ) : users.length === 0 ? (
                    <EmptyState icon={UserPlus} title="No logins yet"
                        text={`Create a login for someone at ${customer.name} so they can raise tickets.`}
                        action={isActive && <Button variant="emphasized" icon={UserPlus} onClick={() => setAdding(true)}>Add login</Button>} />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr><th className={thCls}>Name</th><th className={thCls}>Status</th><th className={`${thCls} hidden xl:table-cell`}>Added</th><th className={`${thCls} text-right`}>Actions</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {users.map(u => {
                                    const off = u.status === "disabled";
                                    return (
                                        <tr key={u.uid}>
                                            <td className={tdCls}>
                                                <span className="flex items-center gap-3">
                                                    <Avatar name={u.name} size={32} />
                                                    <span className="min-w-0">
                                                        <span className={`block font-semibold truncate ${off ? "text-slate-400 line-through" : "text-[#1D2D3E] dark:text-white"}`}>{u.name}</span>
                                                        <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">{u.email}</span>
                                                    </span>
                                                </span>
                                            </td>
                                            <td className={tdCls}><ObjectStatus tone={off ? "neutral" : "positive"}>{off ? "Disabled" : "Active"}</ObjectStatus></td>
                                            <td className={`${tdCls} hidden xl:table-cell text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap`} title={fmtDateTime(u.createdAt)}>{relTime(u.createdAt)}</td>
                                            <td className={`${tdCls} text-right whitespace-nowrap`}>
                                                <Button variant="transparent" icon={KeyRound} onClick={() => resetPassword(u)} disabled={busy} title="Set a new password">Reset password</Button>
                                                <Button variant="transparent" icon={Power} onClick={() => toggleUser(u)} disabled={busy} title={off ? "Enable login" : "Disable login"}>{off ? "Enable" : "Disable"}</Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </TableCard>

            <CustomerTickets customer={customer} />

            {adding && (
                <AddLoginDialog customer={customer} defaultEmail={entry.email || ""} onClose={() => setAdding(false)}
                    onCreated={c => { setAdding(false); setCreated(c); load(); onChanged(); }} />
            )}
        </div>
    );
}

function CustomerTickets({ customer }) {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState(null);
    useEffect(() => {
        api.get("/api/hasbro/tickets", { params: { customerId: customer.id } })
            .then(r => setTickets(r.data.tickets || [])).catch(() => setTickets([]));
    }, [customer.id]);
    const open = (tickets || []).filter(t => !DONE_STATUSES.has(t.status)).length;
    return (
        <TableCard title="Tickets" count={tickets?.length}
            actions={tickets?.length > 0 && <Button variant="transparent" icon={MessageSquare} onClick={() => navigate(`/hasbro/tickets?customer=${customer.id}`)}>Open in Customer Tickets</Button>}>
            {tickets === null ? (
                <div className="p-5"><div className="h-8 rounded-lg bg-slate-100 dark:bg-white/[0.05] animate-pulse" /></div>
            ) : tickets.length === 0 ? (
                <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">No tickets from {customer.name} yet.</p>
            ) : (
                <>
                    <p className="px-5 pt-3 text-xs text-slate-500 dark:text-slate-400">{open} open · {tickets.length - open} finished</p>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead><tr><th className={thCls}>Ticket</th><th className={thCls}>Priority</th><th className={thCls}>Status</th><th className={`${thCls} hidden xl:table-cell`}>Updated</th></tr></thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {tickets.slice(0, 5).map(t => {
                                    const s = statusOf(t.status);
                                    return (
                                        <tr key={t.id}>
                                            <td className={tdCls}>
                                                <span className="block font-semibold text-[#1D2D3E] dark:text-white truncate max-w-[280px]">{t.subject}</span>
                                                <span className="block text-xs text-slate-500 dark:text-slate-400 font-mono">{ticketNumber(t.number)}</span>
                                            </td>
                                            <td className={tdCls}><ObjectStatus tone={PRIORITIES[t.priority]?.tone}>{PRIORITIES[t.priority]?.short}</ObjectStatus></td>
                                            <td className={tdCls}><ObjectStatus tone={s.tone} icon={s.icon}>{s.label}</ObjectStatus></td>
                                            <td className={`${tdCls} hidden xl:table-cell text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap`}>{relTime(t.updatedAt)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </TableCard>
    );
}

// ── Dialogs ───────────────────────────────────────────────────────────────────

function NewCustomerDialog({ onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", contactEmail: "" });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.post("/api/hasbro/customers", form);
            onCreated(data.customer);
        } catch (err) {
            setError(apiError(err, "Could not create the customer."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog title="New customer" onClose={onClose} width="max-w-md"
            footer={<>
                <Button variant="transparent" onClick={onClose}>Cancel</Button>
                <Button variant="emphasized" onClick={submit} disabled={saving}>{saving ? "Creating…" : "Create customer"}</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            <Field label="Customer name">
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={100} className={inputCls} placeholder="e.g. JB Hi-Fi" autoFocus />
            </Field>
            <Field label="Contact email" hint="Optional — for your reference.">
                <input type="email" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} maxLength={254} className={inputCls} />
            </Field>
        </Dialog>
    );
}

function AddLoginDialog({ customer, defaultEmail = "", onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: defaultEmail, password: generatePassword() });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            await api.post(`/api/hasbro/customers/${customer.id}/users`, form);
            onCreated({ email: form.email, password: form.password });
        } catch (err) {
            setError(apiError(err, "Could not create the login."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog title={`Add login · ${customer.name}`} onClose={onClose} width="max-w-md"
            footer={<>
                <Button variant="transparent" onClick={onClose}>Cancel</Button>
                <Button variant="emphasized" onClick={submit} disabled={saving}>{saving ? "Creating…" : "Create login"}</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            <Field label="Full name"><input value={form.name} onChange={set("name")} maxLength={100} className={inputCls} autoFocus /></Field>
            <Field label="Email" hint={defaultEmail ? "From the contact list — change it if the login is for someone else." : undefined}>
                <input type="email" value={form.email} onChange={set("email")} maxLength={254} className={inputCls} placeholder="name@customer.com" />
            </Field>
            <Field label="Password" hint="Generated for you — at least 10 characters. You'll see it once to share.">
                <div className="flex gap-2">
                    <input value={form.password} onChange={set("password")} minLength={10} maxLength={128} className={`${inputCls} font-mono`} />
                    <Button type="button" onClick={() => setForm(f => ({ ...f, password: generatePassword() }))}>New</Button>
                </div>
            </Field>
        </Dialog>
    );
}

function CredentialsStrip({ created, onClose }) {
    const [copied, setCopied] = useState(false);
    const text = `Sign in at ${loginUrl()}\nEmail: ${created.email}\nPassword: ${created.password}`;
    const copy = () => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    return (
        <MessageStrip type="warning" onClose={onClose}>
            <p className="font-semibold">Share these sign-in details with the customer — the password isn't shown again.</p>
            <pre className="mt-2 font-mono text-xs whitespace-pre-wrap break-all bg-white/60 dark:bg-black/20 rounded-lg px-3 py-2">{text}</pre>
            <Button className="mt-2" icon={copied ? Check : Copy} onClick={copy}>{copied ? "Copied" : "Copy details"}</Button>
        </MessageStrip>
    );
}

// Account + Email columns, found by their headings (first 20 rows), so a title row is fine
function readContactRows(buffer) {
    const wb = XLSX.read(buffer, { type: "array" });
    const grid = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: "", raw: false });
    const text = v => String(v || "").trim().toLowerCase();
    const h = grid.slice(0, 20).findIndex(r => r.some(v => /e-?mail/.test(text(v))) && r.some(v => /account|customer|ship ?to|name/.test(text(v))));
    if (h < 0) return null;
    const head = grid[h].map(text);
    const emailCol = head.findIndex(v => /e-?mail/.test(v));
    const accountCol = [/account/, /customer|ship ?to/, /name/].map(re => head.findIndex((v, i) => i !== emailCol && re.test(v))).find(i => i >= 0);
    return grid.slice(h + 1)
        .map((r, i) => ({ account: String(r[accountCol] || "").trim(), email: String(r[emailCol] || "").trim(), sourceRow: h + 2 + i }))
        .filter(r => r.account || r.email);
}

function ImportDialog({ current, onClose, onImported }) {
    const [file, setFile] = useState(null);
    const [rows, setRows] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [report, setReport] = useState(null);

    const pick = async f => {
        setError("");
        setRows(null);
        setFile(f || null);
        if (!f) return;
        try {
            const parsed = readContactRows(await f.arrayBuffer());
            if (!parsed) return setError("Couldn't find the Account and Email columns in this file.");
            setRows(parsed);
        } catch {
            setError("Couldn't read this file. Use a .csv, .xlsx or .xls file.");
        }
    };
    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await api.post("/api/hasbro/contacts/import", { fileName: file?.name, rows });
            setReport(data.report);
            onImported();
        } catch (err) {
            setError(apiError(err, "Could not import the contacts."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog title="Import contacts" onClose={onClose} width="max-w-2xl"
            footer={report ? <Button variant="emphasized" onClick={onClose}>Done</Button> : <>
                <Button variant="transparent" onClick={onClose}>Cancel</Button>
                <Button variant="emphasized" icon={Upload} onClick={submit} disabled={saving || !rows?.length}>{saving ? "Importing…" : `Import ${rows?.length || ""} rows`}</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            {report ? <ImportReport report={report} /> : (
                <>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Upload the ship-to email list (CSV or Excel) with an <b>Account</b> and an <b>Email</b> column. The Control Tower uses it to show the customer contact on each shipment.</p>
                    {current > 0 && <MessageStrip type="warning">This replaces the current list ({current} accounts). Names you linked from the reports and portal accounts are kept for accounts still on the list.</MessageStrip>}
                    <label className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-300 dark:border-white/10 cursor-pointer hover:border-[#0087C9]">
                        <FileSpreadsheet size={22} className="text-[#0087C9] shrink-0" />
                        <span className="flex-1 text-sm text-slate-600 dark:text-slate-300 truncate">{file ? file.name : "Choose a .csv, .xlsx or .xls file"}</span>
                        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => pick(e.target.files?.[0])} />
                        <span className="text-xs font-semibold text-[#0065A0] dark:text-[#6FD0F5]">Browse</span>
                    </label>
                    {rows && (
                        <div className="text-sm">
                            <p className="text-slate-600 dark:text-slate-300 mb-2">{rows.length} rows · {rows.filter(r => r.account).length} with an account name · {rows.filter(r => r.email).length} with an email</p>
                            <div className="rounded-lg border border-slate-200 dark:border-white/[0.07] overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead><tr><th className={thCls}>Row</th><th className={thCls}>Account</th><th className={thCls}>Email</th></tr></thead>
                                    <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                        {rows.slice(0, 5).map(r => <tr key={r.sourceRow}><td className="px-4 py-1.5 text-slate-400">{r.sourceRow}</td><td className="px-4 py-1.5">{r.account || <i className="text-slate-400">blank</i>}</td><td className="px-4 py-1.5">{r.email || <i className="text-slate-400">blank</i>}</td></tr>)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Dialog>
    );
}

// The imported ship-to contact list (e.g. "Ship To MASTER - Specialty HOOS Email List"), in full
const CONTACT_FILTERS = [["all", "All"], ["noemail", "No email"], ["shared", "Shared inbox"], ["notes", "Data notes"], ["portal", "Portal access"]];
const contactNotes = c => (c.notes || []).filter(n => n !== "missing email");
const csvCell = v => `"${String(v ?? "").replace(/"/g, '""')}"`;

function ContactListDialog({ contacts, customers, lastImport, onClose, onImport, onOpen }) {
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [copied, setCopied] = useState(null);
    const byId = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
    const test = useMemo(() => ({
        all: () => true, noemail: c => !c.email, shared: c => c.sharedWith.length > 0,
        notes: c => contactNotes(c).length > 0, portal: c => !!(c.customerId && byId.get(c.customerId)),
    }), [byId]);
    const shown = useMemo(() => {
        const s = q.trim().toLowerCase();
        return contacts.filter(c => test[filter](c)
            && (!s || [c.accountName, c.email, ...(c.aliases || []), ...c.sharedWith].some(v => String(v || "").toLowerCase().includes(s))));
    }, [contacts, q, filter, test]);
    const count = f => contacts.filter(test[f]).length;
    const r = lastImport?.report;

    const copy = email => navigator.clipboard?.writeText(email).then(() => { setCopied(email); setTimeout(() => setCopied(null), 1500); });
    const exportCsv = () => {
        const rows = [["Account", "Email", "Shared inbox with", "Data notes", "Source row", "Portal access"],
            ...shown.map(c => [c.accountName, c.email || "", c.sharedWith.join("; "), contactNotes(c).join("; "), c.sourceRow ?? "", c.customerId && byId.get(c.customerId) ? "Yes" : "No"])];
        const blob = new Blob([rows.map(row => row.map(csvCell).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `hasbro-contact-list-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <Dialog title="Contact list" onClose={onClose} width="max-w-6xl"
            footer={<>
                <Button icon={Upload} onClick={onImport}>Import a new list</Button>
                {contacts.length > 0 && <Button icon={Download} onClick={exportCsv}>Export CSV ({shown.length})</Button>}
                <Button variant="emphasized" onClick={onClose}>Close</Button>
            </>}>
            {!contacts.length ? (
                <EmptyState icon={BookUser} title="No contact list imported"
                    text="Import the ship-to email list (e.g. “Ship To MASTER - Specialty(HOOS Email List).csv”, with Account and Email columns) to see every customer contact here."
                    action={<Button variant="emphasized" icon={Upload} onClick={onImport}>Import contacts</Button>} />
            ) : (
                <>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {lastImport ? <>From {lastImport.fileName ? <b>{lastImport.fileName}</b> : "the last import"} · imported {relTime(lastImport.at)}{lastImport.by?.name ? ` by ${lastImport.by.name}` : ""} · </> : null}
                        {contacts.length} accounts · {contacts.filter(c => c.email).length} with an email
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                        <SearchField value={q} onChange={setQ} placeholder="Search account, email or a linked report name" className="flex-1 min-w-[240px]" />
                        {CONTACT_FILTERS.map(([id, label]) => (
                            <button key={id} onClick={() => setFilter(id)}
                                className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${filter === id
                                    ? "bg-[#0087C9] border-[#0087C9] text-white" : "border-slate-300 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-[#0087C9]"}`}>
                                {label} <span className="opacity-70">{count(id)}</span>
                            </button>
                        ))}
                    </div>
                    <div className="rounded-xl border border-slate-200 dark:border-white/[0.07] overflow-auto max-h-[48vh]">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 z-10">
                                <tr>{["Account", "Email", "Shared inbox with", "Data notes", "Row", "Portal", ""].map(h => <th key={h} className={thCls}>{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-white/[0.05]">
                                {shown.map(c => {
                                    const customer = c.customerId ? byId.get(c.customerId) : null;
                                    return (
                                        <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.03]">
                                            <td className={`${tdCls} font-semibold text-[#1D2D3E] dark:text-white`}>
                                                {c.accountName}
                                                {c.aliases?.length > 0 && <span className="block text-[11px] font-normal text-slate-500 dark:text-slate-400">also: {c.aliases.join(" · ")}</span>}
                                            </td>
                                            <td className={tdCls}>
                                                {c.email ? (
                                                    <span className="flex items-center gap-1.5">
                                                        <a href={`mailto:${c.email}`} className="text-[#0065A0] dark:text-[#6FD0F5] hover:underline break-all">{c.email}</a>
                                                        <button onClick={() => copy(c.email)} className="text-slate-400 hover:text-[#0087C9]" title="Copy email">{copied === c.email ? <Check size={13} /> : <Copy size={13} />}</button>
                                                    </span>
                                                ) : <span className="text-amber-600 dark:text-amber-300">No email on file</span>}
                                            </td>
                                            <td className={`${tdCls} text-xs text-slate-600 dark:text-slate-300 max-w-[220px]`}>{c.sharedWith.join(", ") || "—"}</td>
                                            <td className={`${tdCls} text-xs text-slate-600 dark:text-slate-300 max-w-[200px]`}>{contactNotes(c).join(" · ") || "—"}</td>
                                            <td className={`${tdCls} text-xs text-slate-400 tabular-nums`}>{c.sourceRow ?? "—"}</td>
                                            <td className={tdCls}><PortalStatus customer={customer} /></td>
                                            <td className={`${tdCls} text-right`}>
                                                <Button variant="transparent" onClick={() => onOpen(`c:${c.id}`)}>Open</Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {!shown.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-400">No contacts match.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    {r && (r.unmappedEmails?.length > 0 || r.similarNames?.length > 0 || r.invalidEmails?.length > 0) && (
                        <MessageStrip type="warning">
                            <p className="font-semibold">From the last import — not in the list above</p>
                            {r.unmappedEmails?.length > 0 && <p className="mt-1 text-xs">Emails without an account name: {r.unmappedEmails.map(u => `${u.email} (row ${u.sourceRow})`).join(" · ")}</p>}
                            {r.invalidEmails?.length > 0 && <p className="mt-1 text-xs">Emails that weren't valid: {r.invalidEmails.map(u => `${u.accountName} (row ${u.sourceRow})`).join(" · ")}</p>}
                            {r.similarNames?.length > 0 && <p className="mt-1 text-xs">Names that look like the same store: {r.similarNames.map(p => `${p.a} ↔ ${p.b}`).join(" · ")}</p>}
                        </MessageStrip>
                    )}
                </>
            )}
        </Dialog>
    );
}

function ImportReport({ report: r }) {
    const List = ({ title, items, tone = "warning" }) => items.length > 0 && (
        <MessageStrip type={tone}>
            <p className="font-semibold">{title} ({items.length})</p>
            <p className="mt-1 text-xs leading-relaxed">{items.join(" · ")}</p>
        </MessageStrip>
    );
    return (
        <div className="space-y-3">
            <MessageStrip type="success">
                Imported <b>{r.accounts}</b> accounts — {r.withEmail} with an email{r.removed ? ` · ${r.removed} no longer on the list were removed` : ""}.
            </MessageStrip>
            <List title="No email on file" items={r.missingEmail} />
            <List title="Emails without an account name (not imported)" items={r.unmappedEmails.map(u => `${u.email} (row ${u.sourceRow})`)} />
            <List title="Emails that weren't valid (not imported)" items={r.invalidEmails.map(u => `${u.accountName} (row ${u.sourceRow})`)} tone="error" />
            <List title="Names that look like the same store (typo?)" items={r.similarNames.map(p => `${p.a} ↔ ${p.b}`)} />
            <List title="Same name listed twice — the later row was kept" items={r.duplicateNames} />
            <List title="Shared inboxes — one email for several accounts" items={r.sharedInboxes.map(s => `${s.email}: ${s.accounts.join(", ")}`)} tone="information" />
        </div>
    );
}

function CarriersDialog({ carriers, onClose, onSaved }) {
    const [list, setList] = useState(carriers.map(c => ({ ...c, name: c.name || "" })));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const set = (id, k) => e => setList(l => l.map(c => (c.id === id ? { ...c, [k]: e.target.value } : c)));

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            await api.put("/api/hasbro/carrier-contacts", { carriers: list.map(({ id, name, email }) => ({ id, name, email })) });
            onSaved();
        } catch (err) {
            setError(apiError(err, "Could not save the carrier contacts."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog title="Carrier contacts" onClose={onClose} width="max-w-xl"
            footer={<>
                <Button variant="transparent" onClick={onClose}>Cancel</Button>
                <Button variant="emphasized" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
            </>}>
            {error && <MessageStrip type="error">{error}</MessageStrip>}
            <p className="text-sm text-slate-600 dark:text-slate-300">Shown on each shipment in the Control Tower for its carrier, and used for “Request update”.</p>
            {list.map(c => (
                <div key={c.id} className="rounded-xl border border-slate-200 dark:border-white/[0.07] p-4 space-y-3">
                    <div>
                        <p className="text-sm font-bold text-[#1D2D3E] dark:text-white flex items-center gap-2"><Truck size={15} className="text-[#0087C9]" /> {c.label}</p>
                        {c.id === "mainfreight_nz" && <p className="text-[11px] text-slate-500 mt-0.5 ml-6">Pick it by hand for now — the NZ report will be added later.</p>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Contact name"><input value={c.name} onChange={set(c.id, "name")} maxLength={100} className={inputCls} placeholder="Optional" /></Field>
                        <Field label="Email"><input type="email" value={c.email} onChange={set(c.id, "email")} maxLength={254} className={inputCls} /></Field>
                    </div>
                </div>
            ))}
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5"><BookUser size={13} /> Customer contacts come from the imported contact list.</p>
        </Dialog>
    );
}
